import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useToast } from '@/contexts/ToastContext';
import { useWarehouseStore } from '@/stores/warehouseStore';
import { scrapApi, num, type ScrapStockRow } from '@/api/scrap.api';
import { inr, qtyText, dateText } from '@/pages/manufacturing/scrap/components/scrapUi';

// Scrap yard: scrap physically held, by item, warehouse and yard location, at its scrap value.
// It comes in when scrap is approved and goes out when it is sold, destroyed or donated.

export default function ScrapYardPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { selectedWarehouseId } = useWarehouseStore();
  const [rows, setRows] = useState<ScrapStockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('ALL');

  useEffect(() => {
    setLoading(true);
    scrapApi.stock(selectedWarehouseId || undefined)
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(err instanceof Error ? err.message : 'Could not load the scrap yard'))
      .finally(() => setLoading(false));
  }, [selectedWarehouseId, toast]);

  const locations = useMemo(() => [...new Set(rows.map((r) => r.scrap_location))].sort(), [rows]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => (location === 'ALL' || r.scrap_location === location)
      && (!q || [r.item_name, r.item_code, r.sources, r.warehouse_name].some((v) => String(v || '').toLowerCase().includes(q))));
  }, [rows, search, location]);

  const totalValue = filtered.reduce((a, r) => a + num(r.value), 0);
  const lots = filtered.reduce((a, r) => a + Number(r.open_lots || 0), 0);

  return (
    <AppLayout>
      <div className="p-6 space-y-5 w-full">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-[#1e293b]">Scrap Yard</h1>
            <p className="text-sm text-[#64748b] mt-0.5">Scrap held until it is sold or destroyed — kept apart from usable stock</p>
          </div>
          <button type="button" onClick={() => navigate('/inventory/scrap-management?status=APPROVED')}
            className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 flex items-center gap-1.5 cursor-pointer">
            <i className="ri-hand-coin-line" /> Dispose Scrap
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: 'Scrap Value Held', value: inr(totalValue), icon: 'ri-money-rupee-circle-line', cls: 'bg-indigo-50 text-[#4f46e5]' },
            { label: 'Items', value: filtered.length, icon: 'ri-stack-line', cls: 'bg-amber-50 text-amber-600' },
            { label: 'Lots Waiting Disposal', value: lots, icon: 'ri-inbox-archive-line', cls: 'bg-rose-50 text-rose-600' },
          ].map((c) => (
            <div key={c.label} className="bg-white border border-[#e2e8f0] rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">{c.label}</p>
                <p className="text-2xl font-bold text-[#1e293b] mt-1">{c.value}</p>
              </div>
              <span className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${c.cls}`}><i className={c.icon} /></span>
            </div>
          ))}
        </div>

        <div className="bg-white border border-[#e2e8f0] rounded-xl p-3 flex flex-wrap items-center gap-2">
          <select value={location} onChange={(e) => setLocation(e.target.value)}
            className="h-9 px-2.5 rounded-lg border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5]">
            <option value="ALL">All locations</option>
            {locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <div className="relative flex-1 min-w-[200px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search item, source…"
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#e2e8f0] text-sm focus:outline-none focus:border-[#4f46e5]" />
          </div>
        </div>

        <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead>
                <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                  {['Item', 'Location', 'Qty', 'Avg Scrap Value / Unit', 'Value', 'Sources', 'Last Change', ''].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center text-[#94a3b8]"><i className="ri-loader-4-line animate-spin text-3xl text-[#4f46e5] block mb-2" />Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-12 text-center">
                    <i className="ri-inbox-archive-line text-4xl text-[#e2e8f0] block mb-2" />
                    <p className="text-sm text-[#94a3b8]">The scrap yard is empty</p>
                  </td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.id} className="border-b border-[#f1f5f9] hover:bg-[#f8fafc]">
                    <td className="px-4 py-3">
                      <p className="font-medium text-[#1e293b]">{r.item_name}</p>
                      {r.item_code && <p className="text-[11px] font-mono text-[#94a3b8]">{r.item_code}</p>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-[#1e293b]">{r.scrap_location}</p>
                      <p className="text-[11px] text-[#94a3b8]">{r.warehouse_name || 'No warehouse'}</p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-[#1e293b]">{qtyText(r.qty)} <span className="text-xs font-normal text-[#94a3b8]">{r.unit}</span></td>
                    <td className="px-4 py-3 whitespace-nowrap text-[#475569]">{inr(r.avg_scrap_rate)}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-[#1e293b]">{inr(r.value)}</td>
                    <td className="px-4 py-3 text-xs text-[#64748b] max-w-[240px] truncate" title={r.sources || ''}>
                      {r.sources || '—'} <span className="text-[#94a3b8]">({r.open_lots} lot{r.open_lots === 1 ? '' : 's'})</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-[#94a3b8]">{dateText(r.updated_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button type="button"
                        onClick={() => navigate(`/inventory/scrap-management?status=APPROVED&search=${encodeURIComponent(r.item_name)}`)}
                        className="h-7 px-2.5 rounded-md border border-[#4f46e5] text-[#4f46e5] text-xs font-semibold hover:bg-indigo-50 cursor-pointer">
                        Lots
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
